import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useRateLimitState } from "../../Configs/RateLimitStore";

// Forces a real hard reload — wipes any Cache Storage entries before reloading, so the
// browser can't serve a stale cached bundle alongside the fresh, un-rate-limited requests.
async function hardReload() {
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
  } finally {
    window.location.reload();
  }
}

// Full-screen, non-closable overlay shown whenever the server responds with
// 429 (Too Many Requests). Freezes the whole website for 30s (reverse timer).
// No auto-reload — once the timer hits 0 the "Hard Reload Now" button appears
// and the user has to click it themselves. If more 429s arrive while frozen,
// the timer keeps extending itself (handled in RateLimitStore.triggerFreeze).
function RateLimitOverlay() {
  const { isFrozen, freezeUntil } = useRateLimitState();

  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!isFrozen || !freezeUntil) return;

    const tick = () => {
      const remainingMs = freezeUntil - Date.now();
      const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
      setSecondsLeft(remainingSec);
    };

    tick();
    const intervalId = setInterval(tick, 250);
    return () => clearInterval(intervalId);
  }, [isFrozen, freezeUntil]);

  if (!isFrozen) return null;

  // Rendered via a portal straight onto document.body — some ancestor further up the tree
  // having a CSS transform/filter/overflow can turn itself into the containing block for
  // `position: fixed` descendants, breaking full-viewport coverage. A portal sidesteps that.
  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        backgroundColor: "rgba(0, 0, 0, 0.55)",
        backdropFilter: "blur(2px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        // block all interaction with the page underneath
        pointerEvents: "auto",
      }}
      // swallow any stray clicks/keys on the overlay itself too
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "32px 40px",
          textAlign: "center",
          maxWidth: 420,
          boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            border: "4px solid #e53935",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto 16px",
            fontSize: 28,
            fontWeight: 700,
            color: "#e53935",
          }}
        >
          {secondsLeft}
        </div>
        <h2 style={{ margin: "0 0 8px", fontSize: "1.25rem", color: "#222" }}>
          Too Many Requests
        </h2>
        <p style={{ margin: "0 0 20px", color: "#555", fontSize: "0.95rem" }}>
          You've made too many requests too quickly. The site is temporarily
          locked to protect the server.{" "}
          {secondsLeft > 0 ? (
            <>Please wait <strong>{secondsLeft}s</strong> before reloading.</>
          ) : (
            <>You can hard reload now.</>
          )}
        </p>
        {secondsLeft <= 0 && (
          <button
            type="button"
            onClick={hardReload}
            style={{
              background: "#e53935",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "8px 20px",
              fontSize: "0.9rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Hard Reload Now
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}

export default RateLimitOverlay;
